import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaDollarSign, FaEye, FaEyeSlash } from 'react-icons/fa';
import { updatePhoto } from '../services/api';

const PhotoEditModal = ({ photo, onClose, onSaved, blobUrl }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [isPurchasable, setIsPurchasable] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (photo) {
      setName(photo.name || photo.title || photo.filename?.split('/').pop() || '');
      setPrice(photo.price != null ? photo.price : '');
      setIsPurchasable(!!photo.is_purchasable);
      setVisibility(photo.visibility || 'public');
      setError('');
    }
  }, [photo]);

  if (!photo) return null;

  const handleSave = async () => {
    const nextName = name.trim();
    if (nextName.includes('/') || nextName.includes('\\')) {
      setError('Photo name cannot contain slashes.');
      return;
    }

    const parsedPrice = price === '' ? null : parseFloat(price);
    if (parsedPrice !== null && (isNaN(parsedPrice) || parsedPrice < 0)) {
      setError('Le prix doit être un nombre positif ou rester vide');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await updatePhoto(photo.id, {
        name: nextName || null,
        price: parsedPrice,
        is_purchasable: isPurchasable,
        visibility,
      });
      onSaved(response.photo);
      onClose();
    } catch (err) {
      setError(err.message || "Impossible d'enregistrer les modifications");
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = blobUrl || photo.previewUrl;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF', borderRadius: '14px',
          padding: '2rem', width: '100%', maxWidth: '420px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Modifier la photo</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.2rem', color: 'var(--gray)',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Preview */}
        {previewSrc && (
          <img
            src={previewSrc}
            alt="Aperçu"
            style={{
              width: '100%', height: '180px', objectFit: 'cover',
              borderRadius: '8px', marginBottom: '1.5rem',
              background: 'var(--surface-2)',
            }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>
        )}

        <div className="form-group">
          <label style={{ fontWeight: '600', color: 'var(--dark-gray)' }}>
            Nom de la photo
          </label>
          <input
            type="text"
            maxLength="255"
            placeholder="Saisissez un nom d'affichage"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <small style={{ color: 'var(--gray)', display: 'block', marginTop: '0.35rem' }}>
            This is the name shown in galleries and downloads.
          </small>
        </div>

        {/* Visibility */}
        <div className="form-group">
          <label style={{ fontWeight: '600', color: 'var(--dark-gray)' }}>Visibilité</label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setVisibility('public')}
              style={{
                flex: 1, padding: '0.65rem',
                background: visibility === 'public' ? 'var(--primary-green)' : 'var(--off-white)',
                color: visibility === 'public' ? 'white' : 'var(--dark-gray)',
                border: '2px solid',
                borderColor: visibility === 'public' ? 'var(--primary-green)' : 'var(--light-gray)',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              }}
            >
              <FaEye /> Public
            </button>
            <button
              type="button"
              onClick={() => setVisibility('private')}
              style={{
                flex: 1, padding: '0.65rem',
                background: visibility === 'private' ? 'var(--primary-tint)' : 'var(--surface-2)',
                color: visibility === 'private' ? 'white' : 'var(--dark-gray)',
                border: '2px solid',
                borderColor: visibility === 'private' ? 'var(--primary)' : 'var(--border)',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              }}
            >
              <FaEyeSlash /> Privé
            </button>
          </div>
        </div>

        {/* Price */}
        <div className="form-group">
          <label style={{ fontWeight: '600', color: 'var(--dark-gray)' }}>
            Prix (laisser vide pour gratuit)
          </label>
          <div style={{ position: 'relative' }}>
            <FaDollarSign style={{
              position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)', color: 'var(--gray)',
            }} />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={price}
              onChange={e => setPrice(e.target.value)}
              style={{ paddingLeft: '2rem' }}
            />
          </div>
        </div>

        {/* Is Purchasable */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '1rem', background: 'var(--bg)',
          borderRadius: '8px', marginBottom: '1.5rem',
        }}>
          <input
            id="isPurchasable"
            type="checkbox"
            checked={isPurchasable}
            onChange={e => setIsPurchasable(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label htmlFor="isPurchasable" style={{ cursor: 'pointer', margin: 0 }}>
            <strong>Disponible à l'achat</strong>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray)', marginTop: '2px' }}>
              Autoriser les utilisateurs à acheter cette photo
            </div>
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '0.8rem',
              background: 'var(--surface-2)', color: 'var(--dark-gray)',
              border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontWeight: '600',
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-submit"
            style={{ flex: 1, padding: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            <FaSave /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditModal;
